import {
  Box,
  IconButton,
  Link as MuiLink,
  Tooltip,
  Typography,
  capitalize,
} from "@mui/material";
import { SubjectVariantStat, TemplateStat } from "contexts/Metrics";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { sanitize } from "utils/sanitize";
import { formatEpochTimestamp } from "utils/time";

import styles from "./styles";

interface TemplateCardProps {
  template: Template;
  emailCounts: { [key: number]: number };
  stat?: TemplateStat;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, template: Template) => void;
}

const formatPercentage = (value?: number) =>
  `${Math.round((value || 0) * 100)}%`;

const toPlainText = (html?: string) =>
  (html || "").replace(/<[^>]*>/g, "").trim();

const SUBJECT_BREAKDOWN_HINT =
  "Once you've sent enough emails for multiple subject lines, per-subject metrics will appear.";

const formatRange = (values: number[], fallback?: number) => {
  if (values.length <= 1) return formatPercentage(values[0] ?? fallback);
  const min = Math.round(Math.min(...values) * 100);
  const max = Math.round(Math.max(...values) * 100);
  return min === max ? `${max}%` : `${min}–${max}%`;
};

function SubjectBreakdownTooltip({
  label,
  subjects,
  metric,
}: {
  label: string;
  subjects: SubjectVariantStat[];
  metric: "openedPercentage" | "respondedPercentage";
}) {
  const max = Math.max(...subjects.map((s) => s[metric]), 0);
  return (
    <Box>
      <Typography sx={styles.subjectTooltipTitle}>{label}</Typography>
      {subjects.map((subject, idx) => (
        <Box key={idx} sx={styles.subjectTooltipRow}>
          <Box sx={styles.subjectTooltipHeader}>
            <Box component="span" sx={styles.subjectTooltipLabel}>
              {toPlainText(subject.subjectVariant)}
            </Box>
            <Box component="span" sx={styles.subjectTooltipValue}>
              {formatPercentage(subject[metric])}
            </Box>
          </Box>
          <Box sx={styles.subjectTooltipTrack}>
            <Box
              sx={styles.subjectTooltipFill(
                max > 0 ? (subject[metric] / max) * 100 : 0,
              )}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function HintTooltip({ children }: { children: React.ReactElement }) {
  return (
    <Tooltip
      arrow
      placement="top"
      componentsProps={{
        tooltip: { sx: styles.subjectTooltipPopper },
        arrow: { sx: styles.subjectTooltipArrow },
      }}
      title={
        <Typography sx={styles.subjectTooltipHint}>
          {SUBJECT_BREAKDOWN_HINT}
        </Typography>
      }
    >
      {children}
    </Tooltip>
  );
}

export default function TemplateCard({
  template,
  emailCounts,
  stat,
  onMenuOpen,
}: TemplateCardProps) {
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const isOutreach = template?.contentType === TemplateContentType.OUTREACH;
  const inUseCount =
    template?.id && template.id in emailCounts ? emailCounts[template.id] : 0;

  const subjectCount = template?.subjectVariants?.length ?? 0;
  const hasMultipleSubjects = subjectCount > 1;
  const subjects = stat?.subjects ?? [];
  const showBreakdown = subjects.length > 1;
  // Multiple variants exist, but not enough per-subject data for a breakdown yet.
  const pendingBreakdown = hasMultipleSubjects && !showBreakdown;
  // Backend returns subjects sorted best-reply-rate first.
  const bestSubject = showBreakdown ? subjects[0] : undefined;
  const displaySubject = bestSubject?.subjectVariant ?? template?.subject;
  const openValues = subjects.map((s) => s.openedPercentage);
  const replyValues = subjects.map((s) => s.respondedPercentage);

  return (
    <>
      <Box sx={styles.cardHeader}>
        <Typography
          component="span"
          sx={styles.cardBadge(template?.contentType)}
        >
          {capitalize(template?.contentType)}
        </Typography>
        {hasMultipleSubjects ? (
          <Box component="span" sx={styles.cardSubjectsBadge}>
            <Box component="i" className="fa-solid fa-shuffle" />
            {subjectCount} subjects
          </Box>
        ) : (
          inUseCount > 0 && (
            <Typography sx={styles.cardModified}>
              <Box component="i" className="fa-regular fa-envelope" />{" "}
              {inUseCount} in use
            </Typography>
          )
        )}
      </Box>

      <Typography sx={styles.cardTitle}>{template?.name}</Typography>

      {displaySubject && (
        <Box sx={styles.cardSubjectRow}>
          <Box
            sx={styles.cardSubject}
            dangerouslySetInnerHTML={{ __html: sanitize(displaySubject) }}
          />
          {bestSubject && (
            <Box component="span" sx={styles.cardBestBadge}>
              <Box component="i" className="fa-solid fa-trophy" />
              best
            </Box>
          )}
        </Box>
      )}

      {template?.body && (
        <Box
          sx={styles.cardBody}
          dangerouslySetInnerHTML={{ __html: sanitize(template.body) }}
        />
      )}

      <Box sx={styles.cardDivider} />

      {isOutreach && (
        <Box sx={styles.cardStatsRow}>
          <Box sx={styles.cardStat}>
            {subscription?.isSubscribed ? (
              showBreakdown ? (
                <Tooltip
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: { sx: styles.subjectTooltipPopper },
                    arrow: { sx: styles.subjectTooltipArrow },
                  }}
                  title={
                    <SubjectBreakdownTooltip
                      label="Open Rate by Subject"
                      subjects={subjects}
                      metric="openedPercentage"
                    />
                  }
                >
                  <Typography
                    sx={[styles.cardStatValue(true), styles.cardStatHoverable]}
                  >
                    {formatRange(openValues, stat?.openedPercentage)}
                  </Typography>
                </Tooltip>
              ) : pendingBreakdown ? (
                <HintTooltip>
                  <Typography
                    sx={[styles.cardStatValue(true), styles.cardStatHoverable]}
                  >
                    {formatPercentage(stat?.openedPercentage)}
                  </Typography>
                </HintTooltip>
              ) : (
                <Typography sx={styles.cardStatValue(true)}>
                  {formatPercentage(stat?.openedPercentage)}
                </Typography>
              )
            ) : (
              <Typography sx={styles.cardStatValue(false)}>
                <MuiLink
                  sx={styles.statUpgrade}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUpgradeDialogSource(UpgradeDialogSource.email_tracking);
                  }}
                >
                  Upgrade
                </MuiLink>
              </Typography>
            )}
            <Typography sx={styles.cardStatLabel}>Open rate</Typography>
          </Box>
          <Box sx={styles.cardStat}>
            {showBreakdown ? (
              <Tooltip
                arrow
                placement="top"
                componentsProps={{
                  tooltip: { sx: styles.subjectTooltipPopper },
                  arrow: { sx: styles.subjectTooltipArrow },
                }}
                title={
                  <SubjectBreakdownTooltip
                    label="Reply Rate by Subject"
                    subjects={subjects}
                    metric="respondedPercentage"
                  />
                }
              >
                <Typography
                  sx={[styles.cardStatValue(true), styles.cardStatHoverable]}
                >
                  {formatRange(replyValues, stat?.respondedPercentage)}
                </Typography>
              </Tooltip>
            ) : pendingBreakdown ? (
              <HintTooltip>
                <Typography
                  sx={[styles.cardStatValue(true), styles.cardStatHoverable]}
                >
                  {formatPercentage(stat?.respondedPercentage)}
                </Typography>
              </HintTooltip>
            ) : (
              <Typography sx={styles.cardStatValue(true)}>
                {formatPercentage(stat?.respondedPercentage)}
              </Typography>
            )}
            <Typography sx={styles.cardStatLabel}>Reply rate</Typography>
          </Box>
          <Box sx={styles.cardStat}>
            <Typography sx={styles.cardStatValue(false)}>
              {(stat?.outreachCount ?? 0).toLocaleString()}
            </Typography>
            <Typography sx={styles.cardStatLabel}>Pitches sent</Typography>
          </Box>
        </Box>
      )}

      <Box sx={styles.cardFooter}>
        <Typography sx={styles.cardModified}>
          {template?.updatedAt
            ? `Modified ${formatEpochTimestamp({
                epochTimestamp: template.updatedAt,
                addYear: true,
              })}`
            : "—"}
        </Typography>
        <IconButton
          sx={styles.cardMenuButton}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpen(e, template);
          }}
        >
          <Box component="i" className="fa-solid fa-ellipsis" />
        </IconButton>
      </Box>
    </>
  );
}
